import {
	Container,
	ContainerPadding,
	Text,
	TokenTextAppearance,
	Image,
	TextAlignment,
	AspectRatioContainer,
	CTA,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import React, { useEffect } from 'react';
import { NormalizedVoucher } from '@esportal/portal-service';
import { VouchersHelper, vwDataRedirect } from '../../../../helpers/VouchersHelper';
import { PortalContext } from '../../../../context/portal-context';
import { formatOwnership } from '../../../../tracking/tracking-helper';
import { useTrackingManager } from '../../../../tracking/use-tracking-manager';
import { isVehicle } from '../../../../helpers/common-helpers';

export const VouchersPopup: React.FC<{
	vouchersPopupData: NormalizedVoucher;
	vwBankLayer: () => void;
}> = ({ vouchersPopupData, vwBankLayer }) => {
	const { navigationService, userInteractionService, portalService } = React.useContext(PortalContext);
	const trackingManager = useTrackingManager();

	const voucherVehicle = vouchersPopupData?.vin
		? portalService.getVehicleOrOrderByVin(vouchersPopupData.vin).getValue()
		: undefined;
	const trackingVehicle = isVehicle(voucherVehicle) ? voucherVehicle : undefined;

	useEffect(() => {
		trackingManager.trackClubVWLayerLoad(
			{ contentId: `Voucher Popup Layer: ${vouchersPopupData.layer?.title}` },
			{ vehicle: formatOwnership(trackingVehicle ? [trackingVehicle] : undefined) },
		);
	}, []);

	const layerCta = vouchersPopupData.layer?.cta;
	const isvwBankUrl = layerCta?.src ? /(bedesa-vw\.ceca\.es|volkswagenbank\.es|vwfs\.es)/.test(layerCta.src) : false;

	return (
		<Container
			padding={{
				right: ContainerPadding.dynamic0100,
				bottom: ContainerPadding.dynamic0100,
				left: ContainerPadding.dynamic0100,
			}}
			horizontalAlign={'center'}
			wrap={'always'}
		>
			<AspectRatioContainer data-testid="aspect-ratio-container" ratio="16:9">
				<Image
					src={vouchersPopupData.layer?.imageUrl}
					alt="Imagen bono"
					style={{ borderRadius: '16px', objectFit: 'cover' }}
				/>
			</AspectRatioContainer>
			<Container
				wrap={'always'}
				padding={{
					top: ContainerPadding.dynamic0130,
				}}
			>
				<Text
					appearance={TokenTextAppearance.headline300}
					textAlign={TextAlignment.center}
					color={TokenTextColor.primary}
				>
					{VouchersHelper.buildLiteralMarkdownElement(
						vouchersPopupData.layer?.title ? vouchersPopupData.layer.title : 'No layer title',
					)}
				</Text>

				<Container
					wrap={'always'}
					padding={{
						top: ContainerPadding.dynamic0130,
					}}
					verticalAlign={'center'}
					horizontalAlign={'center'}
				>
					<Text
						appearance={TokenTextAppearance.bigcopy100}
						textAlign={TextAlignment.center}
						color={TokenTextColor.primary}
					>
						{VouchersHelper.buildLiteralMarkdownElement(
							vouchersPopupData.layer?.description ? vouchersPopupData.layer.description : 'No layer description',
						)}
					</Text>
				</Container>

				{vouchersPopupData.layer?.modoEmpleo && (
					<Container
						wrap={'always'}
						padding={{
							top: ContainerPadding.dynamic0130,
						}}
						verticalAlign={'center'}
						horizontalAlign={'center'}
					>
						<Text appearance={TokenTextAppearance.bigcopy100} textAlign={TextAlignment.center} bold>
							¿Cómo puedes utilizarlo?
						</Text>
						<Text
							appearance={TokenTextAppearance.bigcopy100}
							textAlign={TextAlignment.center}
							color={TokenTextColor.primary}
						>
							{VouchersHelper.buildLiteralMarkdownElement(vouchersPopupData.layer?.modoEmpleo)}
						</Text>
					</Container>
				)}

				{layerCta?.src && layerCta.label && (
					<Container
						padding={{
							top: ContainerPadding.dynamic0130,
						}}
						verticalAlign={'center'}
						horizontalAlign={'center'}
					>
						<CTA
							tag="button"
							aria-label="CTA vouchers redirect"
							size="large"
							emphasis="primary"
							onClick={() => {
								trackingManager.trackClubVWButtonClick(
									`Voucher Popup Layer: ${vouchersPopupData.layer?.title}`,
									{
										contentId: `Voucher Popup Layer: ${vouchersPopupData.layer?.title}`,
									},
									{ vehicle: formatOwnership(trackingVehicle ? [trackingVehicle] : undefined) },
								);

								if (isvwBankUrl) {
									vwBankLayer();
								} else {
									vwDataRedirect(vouchersPopupData, navigationService, userInteractionService, layerCta);
								}
							}}
						>
							<Text> {layerCta.label}</Text>
						</CTA>
					</Container>
				)}

				<Container
					wrap={'always'}
					padding={{
						top: ContainerPadding.dynamic0130,
					}}
					verticalAlign={'center'}
					horizontalAlign={'center'}
				>
					<Text
						appearance={TokenTextAppearance.copy100}
						textAlign={TextAlignment.center}
						color={TokenTextColor.tertiary}
					>
						{VouchersHelper.buildLiteralMarkdownElement(vouchersPopupData.legalText)}
					</Text>
				</Container>
			</Container>
		</Container>
	);
};
